import React, { useState } from 'react';
import { Mood } from '@peekapak/types/Mood';
import Tooltip from '../components/Tooltip/Tooltip';
import styles from './ElementaryCheckInModal.module.scss';
import cx from 'classnames';
import happyEmoji from '../images/teacher-check-in/emoticon-happy.png';
import excitedEmoji from '../images/teacher-check-in/emoticon-excited.png';
import sadEmoji from '../images/teacher-check-in/emoticon-sad.png';
import angryEmoji from '../images/teacher-check-in/emoticon-angry.png';
import calmEmoji from '../images/teacher-check-in/emoticon-calm.png';
import nervousEmoji from '../images/teacher-check-in/emoticon-nervous.png';

interface ElementaryCheckInModalProps {
  studentName: string;
  onMoodSelect: (mood: Mood) => void;
  onClose: () => void;
  isSubmitting: boolean;
}

// Elementary moods based on the mockup
const ELEMENTARY_MOODS: { mood: Mood; emojiSrc: string; styleClass: string }[] = [
  { mood: Mood.HAPPY, emojiSrc: happyEmoji, styleClass: 'happy' },
  { mood: Mood.EXCITED, emojiSrc: excitedEmoji, styleClass: 'excited' },
  { mood: Mood.SAD, emojiSrc: sadEmoji, styleClass: 'sad' },
  { mood: Mood.ANGRY, emojiSrc: angryEmoji, styleClass: 'angry' },
  { mood: Mood.CALM, emojiSrc: calmEmoji, styleClass: 'calm' },
  { mood: Mood.NERVOUS, emojiSrc: nervousEmoji, styleClass: 'nervous' },
];

const ElementaryCheckInModal: React.FC<ElementaryCheckInModalProps> = ({
  studentName,
  onMoodSelect,
  onClose,
  isSubmitting,
}) => {
  console.log('ElementaryCheckInModal rendering', { studentName, isSubmitting });

  const [selectedMood, setSelectedMood] = useState<Mood | null>(null);

  React.useEffect(() => {
    console.log('ElementaryCheckInModal mounted');
    // Check if the modal is actually visible
    const timer = setTimeout(() => {
      const modalElement = document.querySelector(`.${styles.modalOverlay}`);
      console.log('Modal element in DOM after render:', modalElement);
      if (modalElement) {
        console.log('Modal element computed styles:', window.getComputedStyle(modalElement as HTMLElement));
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleMoodClick = (mood: Mood) => {
    if (!isSubmitting) {
      setSelectedMood(mood);
      onMoodSelect(mood);
    }
  };

  const handleQuestionMarkClick = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent the button click from firing
  };

  const getMoodDescription = (mood: string) => {
    const descriptions: { [key: string]: string } = {
      Happy: 'Feeling joyful, cheerful, or pleased',
      Excited: 'Feeling enthusiastic, eager, or energetic',
      Sad: 'Feeling unhappy, down, or disappointed',
      Angry: 'Feeling mad, frustrated, or upset',
      Calm: 'Feeling peaceful, relaxed, or quiet',
      Nervous: 'Feeling worried, anxious, or uneasy',
    };
    return descriptions[mood] || '';
  };

  // Try rendering without portal first to isolate the issue
  console.log('About to render modal');

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerText}>
            <h2>How are you feeling right now?</h2>
            <p>Hi {studentName}, click on a face to check in</p>
          </div>
          <button onClick={onClose} disabled={isSubmitting} className={styles.closeButton}>
            <svg fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
            </svg>
          </button>
        </div>

        {/* Mood Grid */}
        <div className={styles.moodGrid}>
          {ELEMENTARY_MOODS.map(({ mood, emojiSrc, styleClass }) => (
            <button
              key={mood}
              onClick={() => handleMoodClick(mood)}
              disabled={isSubmitting}
              className={cx(styles.moodButton, styles[styleClass], {
                [styles.selected]: selectedMood === mood,
              })}
            >
              {/* Question mark tooltip icon */}
              <div className={styles.questionMarkAnchor}>
                <Tooltip content={getMoodDescription(mood)} direction='top'>
                  <div
                    className={styles.questionMark}
                    onClick={handleQuestionMarkClick}
                    onTouchEnd={handleQuestionMarkClick as any}
                  >
                    <svg fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                      />
                    </svg>
                  </div>
                </Tooltip>
              </div>

              {/* Mood content */}
              <div className={styles.moodContent}>
                <img src={emojiSrc} alt={mood} className={styles.emoji} />
                <span className={styles.label}>{mood}</span>
              </div>

              {/* Loading state */}
              {isSubmitting && selectedMood === mood && (
                <div className={styles.loadingOverlay}>
                  <div className={styles.spinner}></div>
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Footer instruction */}
        <div className={styles.footer}>
          <p>Touch a question mark to learn more about each feeling</p>
        </div>

        {/* Loading overlay */}
        {isSubmitting && (
          <div className={styles.submittingOverlay}>
            <div className={styles.submittingContent}>
              <div className={styles.spinner}></div>
              <p>Saving check-in...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ElementaryCheckInModal;
